/**
 * The pattern editor.
 *
 * Drawn on a canvas rather than in the DOM: a 64-row × 32-channel pattern is
 * over two thousand cells, and the playback highlight moves up to thirty times a
 * second. Repainting that as elements drops frames on any machine; repainting it
 * as text on a canvas costs almost nothing and lets the columns line up exactly
 * the way a tracker's do.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  COL_EFFECT,
  COL_INSTRUMENT_HI,
  COL_INSTRUMENT_LO,
  COL_NOTE,
  COL_PARAM_HI,
  COL_PARAM_LO,
  COL_VOLUME_HI,
  COL_VOLUME_LO,
  readCell,
  useTracker,
} from '../../state/store';
import { formatNote } from '../../engine/constants';
import type { Song } from '../../engine/types';

const ROW_HEIGHT = 15;
const FONT_SIZE = 12;
const GUTTER_CHARS = 4;
const CHANNEL_GAP_CHARS = 1;
const HEADER_HEIGHT = 20;

/**
 * Which row sits at the top of the view.
 *
 * The focused row is centred, but clamped at both ends of the pattern —
 * centring without the clamp leaves half the grid blank whenever playback is
 * near the start or end, which reads as a rendering fault rather than a scroll
 * position.
 */
function firstVisibleRow(focusRow: number, totalRows: number, visibleRows: number): number {
  if (totalRows <= visibleRows) return 0;
  const centred = focusRow - Math.floor(visibleRows / 2);
  return Math.max(0, Math.min(totalRows - visibleRows, centred));
}

const COLOURS = {
  background: '#0b0b0e',
  gutter: '#131318',
  gutterText: '#565667',
  beatRow: '#141419',
  barRow: '#191921',
  playRow: 'rgba(6, 193, 103, 0.16)',
  playRowBorder: 'rgba(6, 193, 103, 0.55)',
  selection: 'rgba(6, 193, 103, 0.14)',
  cursor: 'rgba(6, 193, 103, 0.9)',
  cursorCell: 'rgba(6, 193, 103, 0.22)',
  channelDivider: '#1e1e26',
  note: '#e8e8ee',
  noteOff: '#ff6b6b',
  empty: '#33333f',
  instrument: '#4ceb9b',
  volume: '#ffb545',
  effect: '#7aa2ff',
  headerText: '#8a8a9c',
  headerBg: '#101015',
  mutedChannel: '#2a2a33',
};

/** Character offsets of each sub-column within a channel. */
interface ColumnLayout {
  column: number;
  charOffset: number;
  charWidth: number;
}

function layoutFor(format: Song['format']): { columns: ColumnLayout[]; channelChars: number } {
  if (format === 'mod') {
    // "C-4 01 A08"
    return {
      columns: [
        { column: COL_NOTE, charOffset: 0, charWidth: 3 },
        { column: COL_INSTRUMENT_HI, charOffset: 4, charWidth: 1 },
        { column: COL_INSTRUMENT_LO, charOffset: 5, charWidth: 1 },
        { column: COL_EFFECT, charOffset: 7, charWidth: 1 },
        { column: COL_PARAM_HI, charOffset: 8, charWidth: 1 },
        { column: COL_PARAM_LO, charOffset: 9, charWidth: 1 },
      ],
      channelChars: 10,
    };
  }
  // "C-4 01 40 A08"
  return {
    columns: [
      { column: COL_NOTE, charOffset: 0, charWidth: 3 },
      { column: COL_INSTRUMENT_HI, charOffset: 4, charWidth: 1 },
      { column: COL_INSTRUMENT_LO, charOffset: 5, charWidth: 1 },
      { column: COL_VOLUME_HI, charOffset: 7, charWidth: 1 },
      { column: COL_VOLUME_LO, charOffset: 8, charWidth: 1 },
      { column: COL_EFFECT, charOffset: 10, charWidth: 1 },
      { column: COL_PARAM_HI, charOffset: 11, charWidth: 1 },
      { column: COL_PARAM_LO, charOffset: 12, charWidth: 1 },
    ],
    channelChars: 13,
  };
}

/** Effects display as 0-9 then A-Z, the way trackers have always shown them. */
export function effectChar(effect: number): string {
  if (effect === 0) return '0';
  if (effect < 10) return String(effect);
  return String.fromCharCode(55 + effect);
}

function hex2(value: number): string {
  return value.toString(16).toUpperCase().padStart(2, '0');
}

export function PatternEditor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 800, height: 400 });
  const dragging = useRef(false);

  const song = useTracker((s) => s.song);
  const revision = useTracker((s) => s.revision);
  const currentOrder = useTracker((s) => s.currentOrder);
  const cursorRow = useTracker((s) => s.cursorRow);
  const cursorChannel = useTracker((s) => s.cursorChannel);
  const cursorColumn = useTracker((s) => s.cursorColumn);
  const selection = useTracker((s) => s.selection);
  const editMode = useTracker((s) => s.editMode);
  const followPlayback = useTracker((s) => s.followPlayback);
  const playback = useTracker((s) => s.playback);
  const mutedChannels = useTracker((s) => s.mutedChannels);

  const setCursor = useTracker((s) => s.setCursor);
  const beginSelection = useTracker((s) => s.beginSelection);
  const extendSelection = useTracker((s) => s.extendSelection);
  const setSelection = useTracker((s) => s.setSelection);

  const layout = useMemo(() => layoutFor(song.format), [song.format]);
  const pattern = song.patterns[song.order[currentOrder] ?? 0] ?? song.patterns[0];

  // The row the highlight sits on: the playing row when following, else the
  // cursor. They diverge as soon as you scroll away during playback.
  const showPlayRow = playback.playing && followPlayback && playback.order === currentOrder;
  const focusRow = showPlayRow ? playback.row : cursorRow;

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      setSize({ width: element.clientWidth, height: element.clientHeight });
    });
    observer.observe(element);
    setSize({ width: element.clientWidth, height: element.clientHeight });
    return () => observer.disconnect();
  }, []);

  const metrics = useMemo(() => {
    const charWidth = FONT_SIZE * 0.6; // Spline Sans Mono advance at this size
    const gutterWidth = GUTTER_CHARS * charWidth + 8;
    const channelWidth = (layout.channelChars + CHANNEL_GAP_CHARS) * charWidth;
    return { charWidth, gutterWidth, channelWidth };
  }, [layout]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pattern) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const { width, height } = size;
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const { charWidth, gutterWidth, channelWidth } = metrics;
    const headerHeight = HEADER_HEIGHT;
    const visibleRows = Math.floor((height - headerHeight) / ROW_HEIGHT);
    const firstRow = firstVisibleRow(focusRow, pattern.rows, visibleRows);

    ctx.fillStyle = COLOURS.background;
    ctx.fillRect(0, 0, width, height);

    ctx.font = `${FONT_SIZE}px "Spline Sans Mono", ui-monospace, monospace`;
    ctx.textBaseline = 'middle';

    // ---- Channel headers -------------------------------------------------
    ctx.fillStyle = COLOURS.headerBg;
    ctx.fillRect(0, 0, width, headerHeight);
    ctx.fillStyle = COLOURS.headerText;
    for (let ch = 0; ch < song.channels; ch++) {
      const x = gutterWidth + ch * channelWidth;
      if (x > width) break;
      const label = `${mutedChannels[ch] ? '·' : ''}CH${String(ch + 1).padStart(2, '0')}`;
      ctx.fillStyle = mutedChannels[ch] ? COLOURS.mutedChannel : COLOURS.headerText;
      ctx.fillText(label, x + 2, headerHeight / 2);
    }

    // ---- Rows ------------------------------------------------------------
    for (let i = 0; i < visibleRows + 1; i++) {
      const row = firstRow + i;
      if (row < 0 || row >= pattern.rows) continue;
      const y = headerHeight + i * ROW_HEIGHT;

      // Beat and bar shading gives the grid its readable rhythm.
      if (row % 16 === 0) {
        ctx.fillStyle = COLOURS.barRow;
        ctx.fillRect(0, y, width, ROW_HEIGHT);
      } else if (row % 4 === 0) {
        ctx.fillStyle = COLOURS.beatRow;
        ctx.fillRect(0, y, width, ROW_HEIGHT);
      }

      if (showPlayRow && row === playback.row) {
        ctx.fillStyle = COLOURS.playRow;
        ctx.fillRect(0, y, width, ROW_HEIGHT);
        ctx.fillStyle = COLOURS.playRowBorder;
        ctx.fillRect(0, y, width, 1);
        ctx.fillRect(0, y + ROW_HEIGHT - 1, width, 1);
      }

      // Selection wash.
      if (selection && row >= selection.startRow && row <= selection.endRow) {
        const x0 = gutterWidth + selection.startChannel * channelWidth;
        const x1 = gutterWidth + (selection.endChannel + 1) * channelWidth;
        ctx.fillStyle = COLOURS.selection;
        ctx.fillRect(x0, y, x1 - x0, ROW_HEIGHT);
      }

      // Row number gutter.
      ctx.fillStyle = COLOURS.gutter;
      ctx.fillRect(0, y, gutterWidth - 4, ROW_HEIGHT);
      ctx.fillStyle = row % 4 === 0 ? '#7a7a8c' : COLOURS.gutterText;
      ctx.fillText(String(row).padStart(3, '0'), 4, y + ROW_HEIGHT / 2);

      // ---- Cells ---------------------------------------------------------
      for (let ch = 0; ch < song.channels; ch++) {
        const baseX = gutterWidth + ch * channelWidth;
        if (baseX > width) break;
        const cell = readCell(song, pattern, row, ch);
        const dim = mutedChannels[ch];

        // Note
        const noteText = formatNote(cell.note);
        ctx.fillStyle =
          cell.note === 0
            ? COLOURS.empty
            : cell.note === 97
              ? COLOURS.noteOff
              : dim
                ? COLOURS.mutedChannel
                : COLOURS.note;
        ctx.fillText(noteText, baseX, y + ROW_HEIGHT / 2);

        // Instrument
        const instText = cell.instrument === 0 ? '..' : hex2(cell.instrument);
        ctx.fillStyle = cell.instrument === 0 ? COLOURS.empty : dim ? COLOURS.mutedChannel : COLOURS.instrument;
        ctx.fillText(instText, baseX + 4 * charWidth, y + ROW_HEIGHT / 2);

        let effectX = baseX + 7 * charWidth;
        if (song.format !== 'mod') {
          const volText = cell.volume === 0 ? '..' : hex2(cell.volume);
          ctx.fillStyle = cell.volume === 0 ? COLOURS.empty : dim ? COLOURS.mutedChannel : COLOURS.volume;
          ctx.fillText(volText, baseX + 7 * charWidth, y + ROW_HEIGHT / 2);
          effectX = baseX + 10 * charWidth;
        }

        // Effect and parameter read as one three-character group.
        const hasEffect = cell.effect !== 0 || cell.param !== 0;
        const fxText = hasEffect ? effectChar(cell.effect) + hex2(cell.param) : '...';
        ctx.fillStyle = hasEffect ? (dim ? COLOURS.mutedChannel : COLOURS.effect) : COLOURS.empty;
        ctx.fillText(fxText, effectX, y + ROW_HEIGHT / 2);
      }
    }

    // ---- Channel dividers -------------------------------------------------
    ctx.fillStyle = COLOURS.channelDivider;
    for (let ch = 1; ch < song.channels; ch++) {
      const x = gutterWidth + ch * channelWidth - CHANNEL_GAP_CHARS * metrics.charWidth * 0.5;
      if (x > width) break;
      ctx.fillRect(x, headerHeight, 1, height - headerHeight);
    }

    // ---- Cursor -----------------------------------------------------------
    const cursorY = headerHeight + (cursorRow - firstRow) * ROW_HEIGHT;
    if (cursorY >= headerHeight - ROW_HEIGHT && cursorY < height) {
      const col = layout.columns.find((c) => c.column === cursorColumn) ?? layout.columns[0];
      const x = gutterWidth + cursorChannel * channelWidth + col.charOffset * charWidth;
      const w = col.charWidth * charWidth;

      ctx.fillStyle = COLOURS.cursorCell;
      ctx.fillRect(x - 1, cursorY, w + 2, ROW_HEIGHT);
      ctx.strokeStyle = editMode ? COLOURS.cursor : 'rgba(140,140,170,0.8)';
      ctx.lineWidth = editMode ? 2 : 1;
      ctx.strokeRect(x - 1, cursorY + 0.5, w + 2, ROW_HEIGHT - 1);
    }
  }, [
    song,
    pattern,
    revision,
    metrics,
    layout,
    size,
    focusRow,
    cursorRow,
    cursorChannel,
    cursorColumn,
    selection,
    editMode,
    showPlayRow,
    playback.row,
    mutedChannels,
  ]);

  useEffect(() => {
    draw();
  }, [draw]);

  /** Translate a pointer position into a row / channel / column. */
  const hitTest = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !pattern) return null;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      const visibleRows = Math.floor((size.height - HEADER_HEIGHT) / ROW_HEIGHT);
      const firstRow = firstVisibleRow(focusRow, pattern.rows, visibleRows);
      const row = firstRow + Math.floor((y - HEADER_HEIGHT) / ROW_HEIGHT);
      if (row < 0 || row >= pattern.rows) return null;

      const { gutterWidth, channelWidth, charWidth } = metrics;
      const channel = Math.floor((x - gutterWidth) / channelWidth);
      if (channel < 0 || channel >= song.channels) return null;

      const withinChannel = (x - gutterWidth - channel * channelWidth) / charWidth;
      let column = layout.columns[0].column;
      for (const c of layout.columns) {
        if (withinChannel >= c.charOffset && withinChannel < c.charOffset + c.charWidth) {
          column = c.column;
          break;
        }
        if (withinChannel >= c.charOffset) column = c.column;
      }
      return { row, channel, column };
    },
    [pattern, size.height, focusRow, metrics, song.channels, layout],
  );

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const hit = hitTest(event.clientX, event.clientY);
    if (!hit) return;
    canvasRef.current?.focus();
    setCursor(hit.row, hit.channel, hit.column);
    if (event.shiftKey) {
      extendSelection(hit.row, hit.channel);
    } else {
      setSelection(null);
      beginSelection(hit.row, hit.channel);
      dragging.current = true;
    }
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragging.current) return;
    const hit = hitTest(event.clientX, event.clientY);
    if (!hit) return;
    extendSelection(hit.row, hit.channel);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (dragging.current) {
      const hit = hitTest(event.clientX, event.clientY);
      // A click with no drag should not leave a one-cell selection behind.
      if (hit && selection && selection.startRow === hit.row && selection.endRow === hit.row && selection.startChannel === hit.channel && selection.endChannel === hit.channel) {
        setSelection(null);
      }
    }
    dragging.current = false;
  };

  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>) => {
    if (!pattern) return;
    const delta = Math.sign(event.deltaY) * 3;
    const next = Math.max(0, Math.min(pattern.rows - 1, cursorRow + delta));
    setCursor(next, cursorChannel, cursorColumn);
  };

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-ink-950">
      <canvas
        ref={canvasRef}
        style={{ width: size.width, height: size.height }}
        className="block cursor-text outline-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
      />
    </div>
  );
}
